import type { ImageWithMetadata } from 'types/capi/article';

import type { ImageGridDisplayType } from './EditorialImageImpressionEvent';
import InteractionEvent, {
  type InteractionTargetType
} from './InteractionEvent';

export type EditorialImageTargetProperties = {
  clickref?: string;
  cta_type?: string;
  destination_path?: string;
  destination_url?: string;
  display_type: ImageGridDisplayType;
  image_url: string;
  label?: string;
  module_position?: number;
  module_type: 'image-grid';
  position: number;
  retailer_id?: string;
};

type EventData = {
  clickref?: string;
  displayType: ImageGridDisplayType;
  gesture?: string;
  guestId?: string;
  isBot: boolean;
  item: ImageWithMetadata;
  modulePosition?: number;
  position: number;
};

class EditorialImageInteractionEvent extends InteractionEvent<
  EventData,
  EditorialImageTargetProperties
> {
  protected get targetType(): InteractionTargetType {
    return 'editorial_image';
  }

  protected getTargetProperties(): EditorialImageTargetProperties {
    const { clickref, displayType, item, modulePosition, position } = this.data;
    const { cta, desktop, mobile } = item;
    const externalCta = cta?.type === 'external' ? cta : undefined;

    // Every value here is what the impression on the same item sends, so seen and clicked join on
    // (view_id, image_url, module_type, module_position, position): the raw authored destination,
    // never the clickref-tracked one the link navigates to. The clickref rides beside it on an
    // external CTA because that click is a direct click-out with no interstitial; an internal CTA
    // or one with no affiliate mints none and omits the field rather than sending ''. cta_type is
    // the kind the editor authored, carried from CAPI, never derived from the destination.
    return {
      clickref: clickref || undefined,
      cta_type: cta?.cta_type,
      destination_path: cta?.type === 'internal' ? cta.path : undefined,
      destination_url: externalCta?.url,
      display_type: displayType,
      image_url: mobile.url || desktop.url,
      label: cta?.text || undefined,
      module_position: modulePosition,
      module_type: 'image-grid',
      position,
      retailer_id: externalCta?.affiliate?.canonical_id
    };
  }
}

export default EditorialImageInteractionEvent;
